// ./src/components/features/Travels/TravelEditDrawer/TravelEditDrawer.tsx
//
// Edit drawer for Travel records (past trips / completed trips).
//
// Design:
// - Self-loading in edit mode via recordId (shows skeleton while loading)
// - Context-vs-props resolution for get/create/update operations
// - ApiError integration for field-level server validation
// - Deferred open to allow drawer transition before API call
// - Dirty state tracking via form serialization
// - onCreated/onUpdated lightweight notifications for page-level side effects
// - Start/end location managed by two usePlaceLocation hook instances
// - Shared sections: vehicle, travel type, purpose/destination, start/end points,
//   distance mode, comments, tags

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Stack,
  TextInput,
  Textarea,
  NumberInput,
  Group,
  Divider,
  Text,
  Collapse,
  UnstyledButton,
  Switch,
  Box,
  Grid,
  Skeleton,
  SimpleGrid,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { useTranslation } from "react-i18next";
import { OpResult } from "@sdflc/api-helpers";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import {
  DEFAULT_TRAVEL_TYPE,
  TravelTypeValue,
  TRAVEL_STATUSES,
  TravelStatusValue,
} from "@/config";

import {
  utcToLocalString,
  localStringToUtc,
  getCurrentLocalDateTimeString,
} from "@/utils/dateHelpers";
import { ApiError } from "@/utils/apiResultHandler";

import { Travel, TravelInput } from "@/utils/api/TravelService";
import { ExpenseInput, Expense } from "@/utils/api/ExpenseService";

import { usePlaceLocation, PlaceInitialValues } from "@/hooks/usePlaceLocation";
import { AddressData } from "@/hooks/useGoogleMapsAutocomplete";

import { useDashboardContext } from "@/contexts/DashboardContext";
import { useUserProfileContext } from "@/contexts/UserProfileContext";
import { useOptionalTravelsContext } from "@/contexts/TravelsContext";
import { useAppEnvironment } from "@/contexts/AppEnvironmentContext";

import { AppDrawer } from "@/components/generic/AppDrawer";
import { AppIcon } from "@/components/generic/AppIcon";
import { TagsSelectorInput } from "@/components/generic/TagsSelectorInput";

import { CarSelector } from "@/components/features/Cars/CarSelector";
import { PlaceLocationSection } from "@/components/features/PlaceLocationSection";

import { TravelTypeSelector } from "../TravelTypeSelector";
import { TravelStatusBadge } from "../TravelStatusBadge";

// Extend dayjs with UTC plugin
dayjs.extend(utc);

// ============================================
// Types
// ============================================

export interface TravelEditDrawerProps {
  /** Whether the drawer is open */
  opened: boolean;

  /** Function to close the drawer */
  onClose: () => void;

  /** Record ID to load in edit mode (null/undefined for create mode) */
  recordId?: string | null;

  /**
   * Optional override for loading a record by ID.
   * If not provided, uses TravelsContext.get when available.
   */
  onLoad?: (id: string) => Promise<OpResult>;

  /**
   * Optional override for creating a record.
   * If not provided, uses TravelsContext.create when available.
   */
  onCreate?: (data: TravelInput) => Promise<OpResult>;

  /**
   * Optional override for updating a record.
   * If not provided, uses TravelsContext.update when available.
   */
  onUpdate?: (id: string, data: TravelInput) => Promise<OpResult>;

  /**
   * Called after a record is successfully created.
   * Receives the created Travel so the page can react
   * (e.g. highlight the new row).
   */
  onCreated?: (record: Travel) => void;

  /**
   * Called after a record is successfully updated.
   * Receives the updated Travel so the page can react
   * (e.g. highlight the updated row).
   */
  onUpdated?: (record: Travel) => void;

  /** Whether create operation is in progress (prop-based override) */
  isCreating?: boolean;

  /** Whether update operation is in progress (prop-based override) */
  isUpdating?: boolean;

  /** Last error from operations (prop-based override) */
  lastError?: ApiError | null;

  /** Callback to clear errors (prop-based override) */
  onClearError?: () => void;

  /** Pre-selected car ID (from URL param or context) */
  initialCarId?: string | null;
}

// ============================================
// Form Data Interface
// ============================================

interface FormData {
  // Vehicle
  carId: string | null;

  // Travel details
  purpose: string;
  destination: string;
  travelType: TravelTypeValue;
  isRoundTrip: boolean;
  comments: string;

  // Start point (non-location fields)
  startTime: string | null;
  startOdometer: number | null;

  // End point (non-location fields)
  endTime: string | null;
  endOdometer: number | null;

  // Manual distance (when not using odometer)
  useManualDistance: boolean;
  manualDistance: number | null;

  // Tags
  tagIds: string[];

  // Status (for edit mode)
  status: TravelStatusValue;
}

// NOTE: Start/end location fields (whereDone, location, latitude, longitude)
// are now managed by two usePlaceLocation hook instances.

interface ExpandedSections {
  startPoint: boolean;
  endPoint: boolean;
  details: boolean;
  tags: boolean;
}

// ============================================
// Known form fields for ApiError field mapping
// ============================================

const KNOWN_FORM_FIELDS = [
  "carId",
  "purpose",
  "destination",
  "travelType",
  "isRoundTrip",
  "comments",
  "startTime",
  "startOdometer",
  "startWhereDone",
  "startLocation",
  "endTime",
  "endOdometer",
  "endWhereDone",
  "endLocation",
  "manualDistance",
  "distance",
  "distanceIn",
  "tagIds",
  "status",
  "savedPlaceId",
  "savePlace",
  "pointType",
  // Nested firstRecord / lastRecord fields that backend may return errors for
  "firstRecord.odometer",
  "firstRecord.whenDone",
  "firstRecord.whereDone",
  "firstRecord.location",
  "firstRecord.savedPlaceId",
  "firstRecord.pointType",
  "lastRecord.odometer",
  "lastRecord.whenDone",
  "lastRecord.whereDone",
  "lastRecord.location",
  "lastRecord.savedPlaceId",
  "lastRecord.pointType",
];

// ============================================
// Helper Functions
// ============================================

const getInitialFormData = (): FormData => ({
  carId: null,
  purpose: "",
  destination: "",
  travelType: DEFAULT_TRAVEL_TYPE,
  isRoundTrip: false,
  comments: "",
  startTime: getCurrentLocalDateTimeString(),
  startOdometer: null,
  endTime: getCurrentLocalDateTimeString(),
  endOdometer: null,
  useManualDistance: false,
  manualDistance: null,
  tagIds: [],
  status: TRAVEL_STATUSES.COMPLETED,
});

const getInitialExpandedSections = (): ExpandedSections => ({
  startPoint: true,
  endPoint: true,
  details: true,
  tags: true,
});

const serializeFormSnapshot = (data: FormData): string => {
  return JSON.stringify(data);
};

/**
 * Build PlaceInitialValues from an Expense record (firstRecord or lastRecord).
 */
const buildPlaceInitialValuesFromExpense = (
  expense: Expense | null | undefined
): PlaceInitialValues | undefined => {
  if (!expense) return undefined;

  return {
    whereDone: expense.whereDone || "",
    location: expense.location || "",
    latitude: expense.latitude ?? null,
    longitude: expense.longitude ?? null,
    pointType: expense.pointType,
    savedPlaceId: expense.savedPlaceId ?? null,
    addressData: expense.address1
      ? ({
        addressId: "",
        displayName: null,
        address1: expense.address1 || null,
        address2: expense.address2 || null,
        city: expense.city || null,
        country: expense.countryId || null,
        latitude: expense.latitude ?? null,
        longitude: expense.longitude ?? null,
        postalCode: expense.postalCode || null,
        region: expense.stateProvince || null,
      } as AddressData)
      : null,
  };
};

/**
 * Invoke both error-clearing callbacks, but avoid calling the same
 * function reference twice (happens when no prop overrides are provided
 * and both resolve to travelsContext.clearError).
 */
const clearAllErrors = (
  onClearError: (() => void) | undefined,
  clearOperationError: (() => void) | undefined
) => {
  onClearError?.();
  if (clearOperationError && clearOperationError !== onClearError) {
    clearOperationError();
  }
};

// ============================================
// Loading Skeleton Component
// ============================================

const TravelEditDrawerSkeleton: React.FC = () => {
  return (
    <Stack gap="md">
      {/* Status badge skeleton */}
      <Skeleton height={28} width={120} radius="xl" />

      {/* Vehicle selector */}
      <Skeleton height={60} radius="sm" />

      {/* Travel type chips */}
      <Skeleton height={50} radius="sm" />

      {/* Purpose / Destination */}
      <Skeleton height={60} radius="sm" />
      <Skeleton height={60} radius="sm" />

      {/* Round trip switch */}
      <Skeleton height={30} width={200} radius="sm" />

      {/* Start point section */}
      <Divider />
      <Skeleton height={20} width={100} />
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <Skeleton height={60} radius="sm" />
        <Skeleton height={60} radius="sm" />
      </SimpleGrid>
      <Skeleton height={60} radius="sm" />
      <Skeleton height={60} radius="sm" />

      {/* End point section */}
      <Divider />
      <Skeleton height={20} width={100} />
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <Skeleton height={60} radius="sm" />
        <Skeleton height={60} radius="sm" />
      </SimpleGrid>
      <Skeleton height={60} radius="sm" />
      <Skeleton height={60} radius="sm" />

      {/* Distance / Comments / Tags */}
      <Divider />
      <Skeleton height={40} radius="sm" />
      <Divider />
      <Skeleton height={80} radius="sm" />
      <Divider />
      <Skeleton height={40} radius="sm" />
    </Stack>
  );
};

// ============================================
// Section Header Component
// ============================================

interface SectionHeaderProps {
  title: string;
  expanded: boolean;
  onToggle: () => void;
  optional?: boolean;
  badge?: number;
  icon?: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  expanded,
  onToggle,
  optional = false,
  badge,
  icon,
}) => (
  <UnstyledButton onClick={onToggle} w="100%">
    <Group justify="space-between" py="xs">
      <Group gap="xs">
        {icon && <AppIcon iconName={icon} size={18} color="var(--mantine-color-gray-6)" />}
        <Text fw={600} size="sm">
          {title}
        </Text>
        {optional && (
          <Text size="xs" c="dimmed">
            ({optional})
          </Text>
        )}
        {badge !== undefined && badge > 0 && (
          <Text size="xs" c="blue" fw={500}>
            ({badge})
          </Text>
        )}
      </Group>
      <AppIcon iconName={expanded ? "ChevronUp" : "ChevronDown"} size={16} />
    </Group>
  </UnstyledButton>
);

// ============================================
// Component
// ============================================

export const TravelEditDrawer: React.FC<TravelEditDrawerProps> = ({
  opened,
  onClose,
  recordId,
  onLoad,
  onCreate,
  onUpdate,
  onCreated,
  onUpdated,
  isCreating = false,
  isUpdating = false,
  lastError,
  onClearError,
  initialCarId,
}) => {
  const { t } = useTranslation(["travels", "tags", "common", "validation"]);
  const { distanceLabelShort, distanceUnit } = useUserProfileContext();
  const { getCarById, totalCarsCount } = useDashboardContext();
  const { isMobile } = useAppEnvironment();

  // ============================================
  // Context vs Props resolution
  // ============================================

  const travelsContext = useOptionalTravelsContext();

  const loadFn = onLoad ?? travelsContext?.get;
  const createFn = onCreate ?? travelsContext?.create;
  const updateFn = onUpdate ?? travelsContext?.update;
  const creating = isCreating || (travelsContext?.isCreating ?? false);
  const updating = isUpdating || (travelsContext?.isUpdating ?? false);
  const operationError = lastError ?? travelsContext?.lastError ?? null;
  const clearOperationError = onClearError ?? travelsContext?.clearError;

  const isEditMode = Boolean(recordId);
  const isSubmitting = creating || updating;

  // ============================================
  // Stable refs for callbacks used in effects
  // ============================================

  const clearOperationErrorRef = useRef(clearOperationError);
  clearOperationErrorRef.current = clearOperationError;

  const onClearErrorRef = useRef(onClearError);
  onClearErrorRef.current = onClearError;

  const loadFnRef = useRef(loadFn);
  loadFnRef.current = loadFn;

  const tRef = useRef(t);
  tRef.current = t;

  // ============================================
  // Self-loading state
  // ============================================

  const [record, setRecord] = useState<Travel | null>(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [loadError, setLoadError] = useState<ApiError | null>(null);

  const [isReadyToLoad, setIsReadyToLoad] = useState(false);

  useEffect(() => {
    if (opened) {
      const frameId = requestAnimationFrame(() => {
        setIsReadyToLoad(true);
      });
      return () => cancelAnimationFrame(frameId);
    } else {
      setIsReadyToLoad(false);
    }

    return;
  }, [opened]);

  const showLoadingSkeleton = isLoadingRecord && !record;

  // ============================================
  // Load record when opened in edit mode
  // ============================================

  useEffect(() => {
    let isCancelled = false;

    if (isReadyToLoad && isEditMode && recordId && loadFnRef.current) {
      setIsLoadingRecord(true);
      setRecord(null);
      setLoadError(null);

      const loadRecord = async () => {
        const result = await loadFnRef.current!(recordId);

        if (isCancelled) return;

        if (result.didSucceed()) {
          const loaded = result.getDataFirst() as Travel;
          setRecord(loaded);
          setLoadError(null);
        } else {
          setRecord(null);
          setLoadError(ApiError.fromResult(result, tRef.current("travels:form.loadError")));
        }

        setIsLoadingRecord(false);
      };

      loadRecord();
    } else if (!opened) {
      setRecord(null);
      setIsLoadingRecord(false);
      setLoadError(null);
    }

    return () => {
      isCancelled = true;
    };
  }, [isReadyToLoad, opened, isEditMode, recordId]);

  // ============================================
  // Place/Location — Two hook instances
  // ============================================

  const startPlaceLocation = usePlaceLocation({
    enabled: opened && !showLoadingSkeleton,
    autoLocate: false, // Past trip — no GPS
    includePointType: true,
    onFieldChange: () => setPlaceDirty(true),
  });

  const endPlaceLocation = usePlaceLocation({
    enabled: opened && !showLoadingSkeleton,
    autoLocate: false, // Past trip — no GPS
    includePointType: true,
    onFieldChange: () => setPlaceDirty(true),
  });

  // ============================================
  // Form State
  // ============================================

  const [formData, setFormData] = useState<FormData>(() => getInitialFormData());
  const [expandedSections, setExpandedSections] = useState<ExpandedSections>(
    () => getInitialExpandedSections()
  );

  // ============================================
  // Dirty State Tracking
  // ============================================

  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);
  const [placeDirty, setPlaceDirty] = useState(false);

  useEffect(() => {
    if (!opened || showLoadingSkeleton) return;
    const formChanged = serializeFormSnapshot(formData) !== initialSnapshotRef.current;
    setIsDirty(formChanged || placeDirty);
  }, [formData, placeDirty, opened, showLoadingSkeleton]);

  // ============================================
  // Error Handling (ApiError integration)
  // ============================================

  const effectiveError = loadError || operationError;

  const { fields: apiFieldErrors, genericErrors } = useMemo(() => {
    return ApiError.getFormErrors(effectiveError, KNOWN_FORM_FIELDS);
  }, [effectiveError]);

  const formattedGenericError = useMemo(() => {
    return ApiError.formatErrors(genericErrors);
  }, [genericErrors]);

  // Build field error maps for each PlaceLocationSection
  // Map nested backend errors (firstRecord.whereDone) to flat keys (whereDone)
  const startFieldErrors = useMemo(() => ({
    whereDone: apiFieldErrors.startWhereDone || apiFieldErrors["firstRecord.whereDone"],
    location: apiFieldErrors.startLocation || apiFieldErrors["firstRecord.location"],
    savedPlaceId: apiFieldErrors["firstRecord.savedPlaceId"],
    pointType: apiFieldErrors["firstRecord.pointType"],
  }), [apiFieldErrors]);

  const endFieldErrors = useMemo(() => ({
    whereDone: apiFieldErrors.endWhereDone || apiFieldErrors["lastRecord.whereDone"],
    location: apiFieldErrors.endLocation || apiFieldErrors["lastRecord.location"],
    savedPlaceId: apiFieldErrors["lastRecord.savedPlaceId"],
    pointType: apiFieldErrors["lastRecord.pointType"],
  }), [apiFieldErrors]);

  // ============================================
  // Derived State
  // ============================================

  const selectedCar = useMemo(() => {
    if (!formData.carId) return null;
    return getCarById(formData.carId);
  }, [formData.carId, getCarById]);

  const odometerUnit = useMemo(() => {
    return selectedCar?.mileageIn || distanceLabelShort;
  }, [selectedCar?.mileageIn, distanceLabelShort]);

  // ============================================
  // Form Reset on Open
  // ============================================

  useEffect(() => {
    if (!opened) return;

    // In edit mode, wait for record to be loaded before populating form
    if (isEditMode && !record) return;

    let newFormData: FormData;
    let startInitialValues: PlaceInitialValues | undefined;
    let endInitialValues: PlaceInitialValues | undefined;

    if (record) {
      // ── Edit mode ──
      const tagIds =
        record.tags
          ?.map((tag) => tag.id)
          .filter((id): id is string => Boolean(id)) || [];

      const hasOdometer = record.firstOdometer != null;

      newFormData = {
        carId: record.carId || null,
        purpose: record.purpose || "",
        destination: record.destination || "",
        travelType: (record.travelType as TravelTypeValue) || DEFAULT_TRAVEL_TYPE,
        isRoundTrip: record.isRoundTrip || false,
        comments: record.comments || "",
        startTime:
          utcToLocalString(record.firstDttm) || getCurrentLocalDateTimeString(),
        startOdometer: record.firstOdometer ?? null,
        endTime:
          utcToLocalString(record.lastDttm) || getCurrentLocalDateTimeString(),
        endOdometer: record.lastOdometer ?? null,
        useManualDistance: !hasOdometer,
        manualDistance: !hasOdometer ? (record.distance ?? null) : null,
        tagIds,
        status: (record.status as TravelStatusValue) || TRAVEL_STATUSES.COMPLETED,
      };

      // Build place initial values from first/last records
      startInitialValues = buildPlaceInitialValuesFromExpense(record.firstRecord);
      endInitialValues = buildPlaceInitialValuesFromExpense(record.lastRecord);

      // Expand sections that have data
      setExpandedSections({
        startPoint: true,
        endPoint: true,
        details: Boolean(record.purpose || record.destination || record.comments),
        tags: tagIds.length > 0,
      });
    } else {
      // ── Create mode ──
      newFormData = getInitialFormData();

      if (initialCarId) {
        newFormData.carId = initialCarId;
      }

      setExpandedSections(getInitialExpandedSections());
    }

    setFormData(newFormData);
    initialSnapshotRef.current = serializeFormSnapshot(newFormData);
    setIsDirty(false);
    setPlaceDirty(false);

    // Reset both place location hooks
    startPlaceLocation.reset(startInitialValues);
    endPlaceLocation.reset(endInitialValues);

    clearAllErrors(onClearErrorRef.current, clearOperationErrorRef.current);
  }, [opened, record, initialCarId, isEditMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================
  // Handlers
  // ============================================

  const handleFieldChange = useCallback(
    (field: string | undefined, value: string | number | boolean | string[] | null) => {
      if (!field) return;
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleTextInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleFieldChange(e.target.name, e.target.value);
    },
    [handleFieldChange]
  );

  // Number input handlers
  const numberHandlers = useMemo(() => {
    const create = (field: keyof FormData) => (value: number | string) => {
      let numValue: number | null;

      if (value === "" || value === undefined) {
        numValue = null;
      } else if (typeof value === "number") {
        numValue = value;
      } else {
        const parsed = parseFloat(value);
        numValue = isNaN(parsed) ? null : parsed;
      }

      setFormData((prev) => ({ ...prev, [field]: numValue }));
    };

    return {
      startOdometer: create("startOdometer"),
      endOdometer: create("endOdometer"),
      manualDistance: create("manualDistance"),
    };
  }, []);

  // Date handlers
  const dateHandlers = useMemo(() => {
    const create = (field: keyof FormData) => (value: string | null) => {
      handleFieldChange(field, value);
    };

    return {
      startTime: create("startTime"),
      endTime: create("endTime"),
    };
  }, [handleFieldChange]);

  // Section toggle
  const toggleSection = useCallback((section: keyof ExpandedSections) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  }, []);

  // ============================================
  // Submit
  // ============================================

  const handleSubmit = useCallback(async () => {
    clearAllErrors(onClearError, clearOperationError);
    setLoadError(null);

    const startPlaceFields = startPlaceLocation.getPlaceFields();
    const endPlaceFields = endPlaceLocation.getPlaceFields();

    // Build first record (start point)
    const firstRecord: ExpenseInput = {
      carId: formData.carId,
      whenDone: localStringToUtc(formData.startTime),
      whereDone: startPlaceFields.whereDone?.trim() || undefined,
      location: startPlaceFields.location?.trim() || undefined,
      address1: startPlaceFields.address1 || undefined,
      address2: startPlaceFields.address2 || undefined,
      city: startPlaceFields.city || undefined,
      stateProvince: startPlaceFields.stateProvince || undefined,
      postalCode: startPlaceFields.postalCode || undefined,
      countryId: startPlaceFields.countryId || undefined,
      latitude: startPlaceFields.latitude ?? undefined,
      longitude: startPlaceFields.longitude ?? undefined,
      savedPlaceId: startPlaceFields.savedPlaceId || undefined,
      pointType: startPlaceFields.pointType || undefined,
    };

    if (!formData.useManualDistance && formData.startOdometer !== null) {
      firstRecord.odometer = formData.startOdometer;
    }

    // Build last record (end point)
    const lastRecord: ExpenseInput = {
      carId: formData.carId,
      whenDone: localStringToUtc(formData.endTime),
      whereDone: endPlaceFields.whereDone?.trim() || undefined,
      location: endPlaceFields.location?.trim() || undefined,
      address1: endPlaceFields.address1 || undefined,
      address2: endPlaceFields.address2 || undefined,
      city: endPlaceFields.city || undefined,
      stateProvince: endPlaceFields.stateProvince || undefined,
      postalCode: endPlaceFields.postalCode || undefined,
      countryId: endPlaceFields.countryId || undefined,
      latitude: endPlaceFields.latitude ?? undefined,
      longitude: endPlaceFields.longitude ?? undefined,
      savedPlaceId: endPlaceFields.savedPlaceId || undefined,
      pointType: startPlaceFields.pointType || undefined,
    };

    if (!formData.useManualDistance && formData.endOdometer !== null) {
      lastRecord.odometer = formData.endOdometer;
    }

    const submitData: TravelInput = {
      carId: formData.carId,
      purpose: formData.purpose?.trim() || undefined,
      destination: formData.destination?.trim() || undefined,
      travelType: formData.travelType,
      isRoundTrip: formData.isRoundTrip,
      comments: formData.comments?.trim() || undefined,
      tagIds: formData.tagIds.length > 0 ? formData.tagIds : undefined,
      status: formData.status,
      firstRecord,
      lastRecord,
      // Save place applies to both points if either has savePlace
      savePlace: startPlaceFields.savePlace || endPlaceFields.savePlace || undefined,
    };

    if (formData.useManualDistance && formData.manualDistance !== null) {
      submitData.distance = formData.manualDistance;
      submitData.distanceIn = distanceUnit;
    }

    let result: OpResult | null = null;
    let isCreate = false;

    if (isEditMode && recordId && updateFn) {
      result = await updateFn(recordId, submitData);
    } else if (!isEditMode && createFn) {
      result = await createFn(submitData);
      isCreate = true;
    }

    if (result?.didSucceed()) {
      setIsDirty(false);

      const savedRecord = result.data ? (result.data as Travel[])[0] : null;
      if (savedRecord) {
        if (isCreate) {
          onCreated?.(savedRecord);
        } else {
          onUpdated?.(savedRecord);
        }
      }

      onClose();
    }
  }, [
    formData,
    distanceUnit,
    isEditMode,
    recordId,
    startPlaceLocation,
    endPlaceLocation,
    createFn,
    updateFn,
    onClose,
    onClearError,
    clearOperationError,
    onCreated,
    onUpdated,
  ]);

  // Drawer title
  const drawerTitle = useMemo(() => {
    if (isEditMode) {
      return t("travels:form.editTitle");
    }
    return t("travels:form.addPastTitle");
  }, [isEditMode, t]);

  // ============================================
  // Render
  // ============================================

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={drawerTitle}
      position="right"
      size="lg"
      responsive
      mobileHeightPercent={92}
      closable={!isSubmitting && !isLoadingRecord}
      loading={isSubmitting}
      errors={formattedGenericError}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      confirmDisabled={showLoadingSkeleton}
      isDirty={isDirty}
    >
      {showLoadingSkeleton ? (
        <TravelEditDrawerSkeleton />
      ) : (
        <Stack gap="md">
          {/* ============================================ */}
          {/* ESSENTIAL FIELDS - Always visible */}
          {/* ============================================ */}

          {/* Status Badge (Edit mode only) */}
          {isEditMode && record && (
            <Group justify="space-between" align="center">
              <Text size="sm" c="dimmed">
                {t("travels:form.fields.status.label")}
              </Text>
              <TravelStatusBadge status={record.status} />
            </Group>
          )}

          {/* Vehicle Selector */}
          <CarSelector
            label={t("travels:form.fields.car.label")}
            name="carId"
            value={formData.carId}
            onChange={handleFieldChange}
            error={apiFieldErrors.carId}
            disabled={totalCarsCount === 1}
            required
            size="md"
          />

          {/* Travel Type */}
          <TravelTypeSelector
            name="travelType"
            value={formData.travelType}
            onChange={(_, value) => handleFieldChange("travelType", value || DEFAULT_TRAVEL_TYPE)}
            error={apiFieldErrors.travelType}
            required
          />

          {/* ============================================ */}
          {/* TRIP DETAILS */}
          {/* ============================================ */}
          <TextInput
            label={t("travels:form.fields.purpose.label")}
            description={t("travels:form.fields.purpose.description")}
            placeholder={t("travels:form.fields.purpose.placeholder")}
            name="purpose"
            value={formData.purpose}
            onChange={handleTextInputChange}
            error={apiFieldErrors.purpose}
            size="md"
          />

          <TextInput
            label={t("travels:form.fields.destination.label")}
            placeholder={t("travels:form.fields.destination.placeholder")}
            name="destination"
            value={formData.destination}
            onChange={handleTextInputChange}
            error={apiFieldErrors.destination}
            leftSection={<AppIcon iconName="Building" size={18} />}
            size="md"
          />

          <Switch
            label={t("travels:form.fields.isRoundTrip.label")}
            description={t("travels:form.fields.isRoundTrip.description")}
            checked={formData.isRoundTrip}
            onChange={(e) =>
              handleFieldChange("isRoundTrip", e.currentTarget.checked)
            }
            size="md"
          />

          {/* ============================================ */}
          {/* START POINT SECTION */}
          {/* ============================================ */}
          <Divider />
          <SectionHeader
            title={t("travels:form.sections.startPoint")}
            expanded={expandedSections.startPoint}
            onToggle={() => toggleSection("startPoint")}
            icon="MapPinFilled"
          />
          <Collapse in={expandedSections.startPoint}>
            <Stack gap="sm">
              <Grid gutter="sm">
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <DateTimePicker
                    label={t("travels:form.fields.startTime.label")}
                    placeholder={t("travels:form.fields.startTime.placeholder")}
                    value={formData.startTime}
                    onChange={dateHandlers.startTime}
                    error={apiFieldErrors.startTime || apiFieldErrors["firstRecord.whenDone"]}
                    maxDate={new Date()}
                    size="md"
                    required
                    withAsterisk
                    valueFormat="MMM DD, YYYY HH:mm"
                    dropdownType={isMobile ? "modal" : "popover"}
                    timePickerProps={{
                      withDropdown: true,
                      popoverProps: { withinPortal: true },
                      format: "12h",
                    }}
                  />
                </Grid.Col>
                {!formData.useManualDistance && (
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <NumberInput
                      label={`${t("travels:form.fields.startOdometer.label")} (${odometerUnit})`}
                      placeholder={t("travels:form.fields.startOdometer.placeholder")}
                      value={formData.startOdometer ?? ""}
                      onChange={numberHandlers.startOdometer}
                      error={apiFieldErrors.startOdometer || apiFieldErrors["firstRecord.odometer"]}
                      min={0}
                      max={9999999}
                      thousandSeparator=","
                      size="md"
                      hideControls
                    />
                  </Grid.Col>
                )}
              </Grid>

              {/* Start Place, Address, Map Preview, Save Toggle */}
              <PlaceLocationSection
                placeLocation={startPlaceLocation}
                showPointType
                showSaveToggle={!isEditMode}
                fieldErrors={startFieldErrors}
                placeNameLabel={t("travels:form.fields.startWhereDone.label")}
                placeNamePlaceholder={t("travels:form.fields.startWhereDone.placeholder")}
                addressLabel={t("travels:form.fields.startLocation.label")}
                addressPlaceholder={t("travels:form.fields.startLocation.placeholder")}
                mapHeight={100}
              />
            </Stack>
          </Collapse>

          {/* ============================================ */}
          {/* END POINT SECTION */}
          {/* ============================================ */}
          <Divider />
          <SectionHeader
            title={t("travels:form.sections.endPoint")}
            expanded={expandedSections.endPoint}
            onToggle={() => toggleSection("endPoint")}
            icon="MapPin"
          />
          <Collapse in={expandedSections.endPoint}>
            <Stack gap="sm">
              <Grid gutter="sm">
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <DateTimePicker
                    label={t("travels:form.fields.endTime.label")}
                    placeholder={t("travels:form.fields.endTime.placeholder")}
                    value={formData.endTime}
                    onChange={dateHandlers.endTime}
                    error={apiFieldErrors.endTime || apiFieldErrors["lastRecord.whenDone"]}
                    maxDate={new Date()}
                    size="md"
                    required
                    withAsterisk
                    valueFormat="MMM DD, YYYY HH:mm"
                    dropdownType={isMobile ? "modal" : "popover"}
                    timePickerProps={{
                      withDropdown: true,
                      popoverProps: { withinPortal: true },
                      format: "12h",
                    }}
                  />
                </Grid.Col>
                {!formData.useManualDistance && (
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <NumberInput
                      label={`${t("travels:form.fields.endOdometer.label")} (${odometerUnit})`}
                      placeholder={t("travels:form.fields.endOdometer.placeholder")}
                      value={formData.endOdometer ?? ""}
                      onChange={numberHandlers.endOdometer}
                      error={apiFieldErrors.endOdometer || apiFieldErrors["lastRecord.odometer"]}
                      min={formData.startOdometer || 0}
                      max={9999999}
                      thousandSeparator=","
                      size="md"
                      hideControls
                    />
                  </Grid.Col>
                )}
              </Grid>

              {/* End Place, Address, Map Preview, Save Toggle */}
              <PlaceLocationSection
                placeLocation={endPlaceLocation}
                showPointType
                showSaveToggle={!isEditMode}
                fieldErrors={endFieldErrors}
                placeNameLabel={t("travels:form.fields.endWhereDone.label")}
                placeNamePlaceholder={t("travels:form.fields.endWhereDone.placeholder")}
                addressLabel={t("travels:form.fields.endLocation.label")}
                addressPlaceholder={t("travels:form.fields.endLocation.placeholder")}
                mapHeight={100}
              />
            </Stack>
          </Collapse>

          {/* ============================================ */}
          {/* DISTANCE MODE SWITCH */}
          {/* ============================================ */}
          <Divider />
          <Box
            p="sm"
            style={{
              backgroundColor: "var(--mantine-color-gray-0)",
              borderRadius: "var(--mantine-radius-sm)",
            }}
          >
            <Group justify="space-between" wrap="nowrap">
              <Stack gap={2}>
                <Text size="sm" fw={500}>
                  {t("travels:form.fields.useManualDistance.label")}
                </Text>
                <Text size="xs" c="dimmed">
                  {t("travels:form.fields.useManualDistance.description")}
                </Text>
              </Stack>
              <Switch
                checked={formData.useManualDistance}
                onChange={(e) =>
                  handleFieldChange("useManualDistance", e.currentTarget.checked)
                }
                size="md"
              />
            </Group>
          </Box>

          {/* Manual Distance Input */}
          {formData.useManualDistance && (
            <NumberInput
              label={`${t("travels:form.fields.manualDistance.label")} (${odometerUnit})`}
              placeholder={t("travels:form.fields.manualDistance.placeholder")}
              value={formData.manualDistance ?? ""}
              onChange={numberHandlers.manualDistance}
              error={apiFieldErrors.manualDistance || apiFieldErrors.distance}
              min={0}
              max={99999}
              decimalScale={1}
              thousandSeparator=","
              size="md"
              hideControls
            />
          )}

          {/* ============================================ */}
          {/* NOTES SECTION */}
          {/* ============================================ */}
          <Divider />
          <Textarea
            label={t("travels:form.fields.comments.label")}
            placeholder={t("travels:form.fields.comments.placeholder")}
            name="comments"
            value={formData.comments}
            onChange={handleTextInputChange}
            error={apiFieldErrors.comments}
            minRows={2}
            maxRows={4}
            autosize
            size="md"
          />

          {/* ============================================ */}
          {/* TAGS SECTION */}
          {/* ============================================ */}
          <Divider />
          <TagsSelectorInput
            name="tagIds"
            value={formData.tagIds}
            onChange={handleFieldChange}
            label={t("travels:form.fields.tags.label")}
            placeholder={t("travels:form.fields.tags.placeholder")}
            maxTags={5}
            allowCreate
            size="md"
          />
        </Stack>
      )}
    </AppDrawer>
  );
};